import { useState, useCallback, useEffect, useRef } from "react";
import type { RankedSpace } from "../../ai-mode/types";
import { useProposalT } from "../i18n/ProposalI18nContext";

interface PhotosTabProps {
  selectedSpace: RankedSpace;
  displayName: string;
  images: string[];
  currentImageIndex: number;
  setCurrentImageIndex: React.Dispatch<React.SetStateAction<number>>;
  monthlyPrice: number;
  pricePerPos: number;
  hidePrice?: boolean;
}

/**
 * The absolute photo indices shown as dots. Up to 7 dots; beyond that a sliding
 * window of 7 keeps the active dot centered (clamped at the ends), per the
 * carousel spec — the window edges shrink to hint there are more photos.
 */
function buildDotWindow(total: number, pos: number): number[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  let start: number;
  if (pos < 3) start = 0;
  else if (pos >= total - 3) start = total - 7;
  else start = pos - 3;
  return Array.from({ length: 7 }, (_, i) => start + i);
}

/** Dot diameter by its position within a 7-slot window: edges 4px, then 6px, center 8px. */
function dotSize(windowPos: number, total: number): number {
  if (total <= 7) return 8;
  if (windowPos === 0 || windowPos === 6) return 4;
  if (windowPos === 1 || windowPos === 5) return 6;
  return 8;
}

/** Shared visual for the circular hover nav arrows (left/right + opacity set per side). */
const arrowStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: "26px",
  height: "26px",
  borderRadius: "999px",
  border: 0,
  background: "rgba(255,255,255,0.9)",
  color: "#1a1a17",
  backdropFilter: "blur(4px)",
  boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  zIndex: 8,
  transition: "opacity 0.15s",
};

export function PhotosTab({
  selectedSpace,
  displayName,
  images,
  currentImageIndex,
  setCurrentImageIndex,
  monthlyPrice,
  pricePerPos,
  hidePrice = false,
}: PhotosTabProps) {
  const { t } = useProposalT();
  // Track failed photos by URL, not index: the Escritório/Espaço toggle swaps the
  // image set in place, so index-based tracking would let a failed index from one
  // set hide a valid photo in the other. URLs are unique per photo, so stale
  // entries simply never match the current set — no reset needed, no flicker.
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const lightboxRef = useRef<HTMLDivElement>(null);

  // Warm the browser cache for the whole gallery so clicking through the arrows/
  // dots swaps instantly instead of fetching each full-size photo on demand.
  const imagesKey = images.join("|");
  useEffect(() => {
    if (typeof Image === "undefined") return;
    for (const url of images) {
      const preloader = new Image();
      preloader.src = url;
    }
    // Only re-run when the set of URLs actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesKey]);

  const validImages = images
    .map((url, i) => ({ url, index: i }))
    .filter(({ url }) => !failedUrls.has(url));

  const handleImageError = useCallback(
    (failedIndex: number) => {
      const failedUrl = images[failedIndex];
      if (failedUrl == null) return;
      setFailedUrls((prev) => new Set(prev).add(failedUrl));
      if (failedIndex === currentImageIndex) {
        const remaining = images
          .map((url, i) => ({ url, i }))
          .filter(({ url }) => url !== failedUrl && !failedUrls.has(url));
        if (remaining.length > 0) {
          const nextValid =
            remaining.find((r) => r.i > failedIndex) ?? remaining[0];
          setCurrentImageIndex(nextValid.i);
        }
      }
    },
    [images, currentImageIndex, failedUrls, setCurrentImageIndex],
  );

  // Current position within the visible (non-failed) photos.
  const currentPos = Math.max(
    0,
    validImages.findIndex((v) => v.index === currentImageIndex),
  );
  // Desktop shows nav arrows only on hover; touch devices hide them and swipe.
  const [isHovered, setIsHovered] = useState(false);
  // Detect touch via the pointer/hover media query — `'ontouchstart' in window`
  // is unreliable (true on desktop Chrome), which would wrongly hide the arrows.
  const [isTouch] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(hover: none)").matches === true,
  );
  const touchStartX = useRef<number | null>(null);

  const goToPos = useCallback(
    (pos: number) => {
      if (pos < 0 || pos >= validImages.length) return;
      setCurrentImageIndex(validImages[pos].index);
    },
    [validImages, setCurrentImageIndex],
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const deltaX = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (deltaX < -40) goToPos(currentPos + 1);
    else if (deltaX > 40) goToPos(currentPos - 1);
  };

  return (
    <div
      role="tabpanel"
      className="rounded-2xl overflow-hidden"
      style={{ border: "1px solid #E8E6E3", background: "white" }}
    >
      {/* Photo area */}
      <div
        className="relative h-52 sm:h-[380px]"
        style={{ background: "#f3f4f6" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={isTouch ? handleTouchStart : undefined}
        onTouchEnd={isTouch ? handleTouchEnd : undefined}
      >
        {validImages.length > 0 ? (
          <img
            src={images[currentImageIndex]}
            alt={displayName}
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => setLightboxOpen(true)}
            onError={() => handleImageError(currentImageIndex)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            {t("photos.noImage")}
          </div>
        )}

        {/* Zoom / expand icon */}
        {validImages.length > 0 && (
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute bottom-4 right-4 flex items-center justify-center rounded-lg transition-opacity hover:opacity-100 opacity-80"
            style={{
              width: "36px",
              height: "36px",
              background: "rgba(0,0,0,0.5)",
              border: "none",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
            }}
            aria-label="Ampliar foto"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </button>
        )}

        {/* Nav arrows — desktop only, revealed on hover; hidden on touch (swipe
            navigates). Bounded: no arrow past the first/last photo. */}
        {validImages.length > 1 && !isTouch && (
          <>
            {currentPos > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPos(currentPos - 1);
                }}
                aria-label="Foto anterior"
                style={{
                  ...arrowStyle,
                  left: "5px",
                  opacity: isHovered ? 1 : 0,
                  pointerEvents: isHovered ? "auto" : "none",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            {currentPos < validImages.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPos(currentPos + 1);
                }}
                aria-label="Próxima foto"
                style={{
                  ...arrowStyle,
                  right: "5px",
                  opacity: isHovered ? 1 : 0,
                  pointerEvents: isHovered ? "auto" : "none",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </>
        )}

        {/* Dot indicators — sliding window of up to 7, graduated size/opacity.
            Always visible (desktop and mobile). */}
        {validImages.length > 1 && (
          <div
            style={{
              position: "absolute",
              bottom: "8px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              zIndex: 9,
            }}
          >
            {buildDotWindow(validImages.length, currentPos).map((abs, p) => {
              const total = validImages.length;
              const isActive = abs === currentPos;
              const isOverflowEdge =
                total > 7 &&
                ((p === 0 && abs > 0) || (p === 6 && abs < total - 1));
              const size = dotSize(p, total);
              return (
                <button
                  key={abs}
                  onClick={() => goToPos(abs)}
                  aria-label={`Foto ${abs + 1}`}
                  aria-current={isActive}
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    flexShrink: 0,
                    padding: 0,
                    borderRadius: "999px",
                    border: "none",
                    cursor: "pointer",
                    background: "#fff",
                    opacity: isActive ? 1 : isOverflowEdge ? 0.3 : 0.45,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.45)",
                    transition:
                      "width 0.2s ease, height 0.2s ease, opacity 0.2s ease",
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Values area */}
      <div style={{ padding: "20px 24px" }}>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-3">
          <div>
            <span className="text-lg font-bold" style={{ color: "#1A1814" }}>
              {displayName}
            </span>
            <span
              className="text-sm"
              style={{ color: "#78736D", marginLeft: "8px" }}
            >
              {selectedSpace.capacity} {t("photos.positions")}
            </span>
          </div>
          {!hidePrice && monthlyPrice > 0 && (
            <span className="text-xl font-bold" style={{ color: "#1A1814" }}>
              R$ {monthlyPrice.toLocaleString("pt-BR")}
              <span
                className="text-xs font-normal"
                style={{ color: "#78736D" }}
              >
                {t("photos.perMonth")}
              </span>
            </span>
          )}
        </div>

        {!hidePrice && (
          <>
            {/* Cost composition bar */}
            <div
              className="rounded-lg overflow-hidden"
              style={{ height: "10px", display: "flex" }}
            >
              <div
                style={{ width: "53%", background: "#B8860B" }}
                title={t("photos.costRent")}
              />
              <div
                style={{ width: "32%", background: "#FFD900" }}
                title={t("photos.costServices")}
              />
              <div
                style={{ width: "15%", background: "#78736D" }}
                title={t("photos.costSupport")}
              />
            </div>
            <div className="flex gap-4 mt-2 flex-wrap">
              <div className="flex items-center gap-1">
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "2px",
                    background: "#B8860B",
                  }}
                />
                <span className="text-xs" style={{ color: "#9E9A94" }}>
                  {t("photos.costRent")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "2px",
                    background: "#FFD900",
                  }}
                />
                <span className="text-xs" style={{ color: "#9E9A94" }}>
                  {t("photos.costServices")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "2px",
                    background: "#78736D",
                  }}
                />
                <span className="text-xs" style={{ color: "#9E9A94" }}>
                  {t("photos.costSupport")}
                </span>
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: "#78736D" }}>
              {pricePerPos > 0
                ? t("photos.perPosition", {
                    price: pricePerPos.toLocaleString("pt-BR"),
                  })
                : t("photos.priceOnRequest")}
            </p>
          </>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightboxOpen && validImages.length > 0 && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={(e) => {
            if (e.target === lightboxRef.current) setLightboxOpen(false);
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 flex items-center justify-center rounded-xl transition-colors hover:bg-white/20"
            style={{
              width: "44px",
              height: "44px",
              background: "rgba(255,255,255,0.1)",
              border: "none",
              cursor: "pointer",
              zIndex: 51,
            }}
            aria-label="Fechar"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Image */}
          <img
            src={images[currentImageIndex]}
            alt={displayName}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl"
            style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
          />

          {/* Nav arrows */}
          {validImages.length > 1 && (
            <>
              <button
                onClick={() => {
                  const curIdx = validImages.findIndex(
                    (v) => v.index === currentImageIndex,
                  );
                  const prevIdx =
                    curIdx <= 0 ? validImages.length - 1 : curIdx - 1;
                  setCurrentImageIndex(validImages[prevIdx].index);
                }}
                className="absolute left-4 flex items-center justify-center rounded-full transition-colors hover:bg-white/20"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(255,255,255,0.12)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => {
                  const curIdx = validImages.findIndex(
                    (v) => v.index === currentImageIndex,
                  );
                  const nextIdx =
                    curIdx >= validImages.length - 1 ? 0 : curIdx + 1;
                  setCurrentImageIndex(validImages[nextIdx].index);
                }}
                className="absolute right-4 flex items-center justify-center rounded-full transition-colors hover:bg-white/20"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(255,255,255,0.12)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </>
          )}

          {/* Counter */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/70 font-medium px-3 py-1 rounded-full"
            style={{ background: "rgba(255,255,255,0.1)" }}
          >
            {validImages.findIndex((v) => v.index === currentImageIndex) + 1} /{" "}
            {validImages.length}
          </div>
        </div>
      )}
    </div>
  );
}
